/**
 * Vault API boundary.
 *
 * In mock mode this still serves fixture-backed data. With `USE_MOCK_API` false it reads
 * and writes the backend Vault endpoints, including per-user Vault activity and admin CRUD.
 */
import { USE_MOCK_API } from '../config';
import { VAULT_CATEGORIES, VAULT_PACKS } from '../data/vault';
import { newId } from '../lib/ids';
import {
  createMockBonus,
  deleteMockBonus,
  getMockBonus,
  listMockBonuses,
  updateMockBonus,
} from './mockBonuses';
import type {
  BonusClaimRecipient,
  BonusPack,
  VaultActivity,
  VaultCategory,
  VaultPack,
} from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

const EMPTY_ACTIVITY: VaultActivity = {
  favoritePacks: [],
  favoriteItems: [],
  downloads: [],
  recentPacks: [],
  recentPrompts: [],
  recentPresets: [],
};

let mockPacks: VaultPack[] = VAULT_PACKS.map((pack) => ({ ...pack, published: pack.published ?? true }));
let mockActivity: VaultActivity = { ...EMPTY_ACTIVITY };

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function requireMockPack(id: string): VaultPack {
  const pack = mockPacks.find((candidate) => candidate.id === id);
  if (!pack) throw new ApiError(404, `Unknown vault pack "${id}"`, 'not_found');
  return pack;
}

export const vaultApi = {
  async getCategories(): Promise<VaultCategory[]> {
    if (!USE_MOCK_API) {
      return http.get<VaultCategory[]>('/vault/categories');
    }
    assertMock('vaultApi.getCategories');
    return mockDelay(clone(VAULT_CATEGORIES));
  },

  async getPacks(): Promise<VaultPack[]> {
    if (!USE_MOCK_API) {
      return http.get<VaultPack[]>('/vault/packs');
    }
    assertMock('vaultApi.getPacks');
    return mockDelay(clone(mockPacks.filter((pack) => pack.published !== false)));
  },

  async getPack(id: string): Promise<VaultPack> {
    if (!USE_MOCK_API) {
      return http.get<VaultPack>(`/vault/packs/${id}`);
    }
    assertMock('vaultApi.getPack');
    return mockDelay(clone(requireMockPack(id)));
  },

  async getMe(): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.get<VaultActivity>('/vault/me');
    }
    assertMock('vaultApi.getMe');
    return mockDelay(clone(mockActivity));
  },

  async recordPackDownload(id: string): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.post<VaultActivity>(`/vault/packs/${id}/download`);
    }
    assertMock('vaultApi.recordPackDownload');
    const pack = requireMockPack(id);
    mockActivity = {
      ...mockActivity,
      downloads: [
        { id: newId(), packId: pack.id, name: pack.name, version: pack.version, date: 'Just now' },
        ...mockActivity.downloads,
      ],
    };
    return mockDelay(clone(mockActivity));
  },

  async setPackFavorite(id: string, favorite: boolean): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.request<VaultActivity>(`/vault/favorites/${id}`, { method: 'PUT', body: { favorite } });
    }
    assertMock('vaultApi.setPackFavorite');
    mockActivity = {
      ...mockActivity,
      favoritePacks: favorite
        ? [...new Set([...mockActivity.favoritePacks, id])]
        : mockActivity.favoritePacks.filter((candidate) => candidate !== id),
    };
    return mockDelay(clone(mockActivity));
  },

  async setItemFavorite(id: string, favorite: boolean): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.request<VaultActivity>(`/vault/favorites/items/${id}`, { method: 'PUT', body: { favorite } });
    }
    assertMock('vaultApi.setItemFavorite');
    mockActivity = {
      ...mockActivity,
      favoriteItems: favorite
        ? [...new Set([...mockActivity.favoriteItems, id])]
        : mockActivity.favoriteItems.filter((candidate) => candidate !== id),
    };
    return mockDelay(clone(mockActivity));
  },

  async noteRecentPack(packId: string): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.post<VaultActivity>(`/vault/recent/packs/${packId}`);
    }
    assertMock('vaultApi.noteRecentPack');
    mockActivity = {
      ...mockActivity,
      recentPacks: [packId, ...mockActivity.recentPacks.filter((candidate) => candidate !== packId)].slice(0, 8),
    };
    return mockDelay(clone(mockActivity));
  },

  async noteRecentPrompt(packId: string, promptId: string, title: string): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.post<VaultActivity>(`/vault/recent/prompts/${promptId}`, { packId, title });
    }
    assertMock('vaultApi.noteRecentPrompt');
    mockActivity = {
      ...mockActivity,
      recentPrompts: [
        { id: promptId, packId, title, date: 'Just now' },
        ...mockActivity.recentPrompts.filter((candidate) => candidate.id !== promptId),
      ].slice(0, 8),
    };
    return mockDelay(clone(mockActivity));
  },

  async noteRecentPreset(packId: string, presetId: string, name: string): Promise<VaultActivity> {
    if (!USE_MOCK_API) {
      return http.post<VaultActivity>(`/vault/recent/presets/${presetId}`, { packId, name });
    }
    assertMock('vaultApi.noteRecentPreset');
    mockActivity = {
      ...mockActivity,
      recentPresets: [
        { id: presetId, packId, name, date: 'Just now' },
        ...mockActivity.recentPresets.filter((candidate) => candidate.id !== presetId),
      ].slice(0, 8),
    };
    return mockDelay(clone(mockActivity));
  },

  async getAdminPacks(): Promise<VaultPack[]> {
    if (!USE_MOCK_API) {
      return http.get<VaultPack[]>('/admin/vault/packs');
    }
    assertMock('vaultApi.getAdminPacks');
    return mockDelay(clone(mockPacks));
  },

  async createAdminPack(pack: VaultPack): Promise<VaultPack> {
    if (!USE_MOCK_API) {
      return http.post<VaultPack>('/admin/vault/packs', pack);
    }
    assertMock('vaultApi.createAdminPack');
    const created = { ...clone(pack), id: pack.id || newId(), published: pack.published ?? true };
    mockPacks = [created, ...mockPacks];
    return mockDelay(clone(created));
  },

  async updateAdminPack(id: string, patch: Partial<VaultPack>): Promise<VaultPack> {
    if (!USE_MOCK_API) {
      return http.patch<VaultPack>(`/admin/vault/packs/${id}`, patch);
    }
    assertMock('vaultApi.updateAdminPack');
    const pack = requireMockPack(id);
    const updated = { ...pack, ...clone(patch), id: pack.id };
    mockPacks = mockPacks.map((candidate) => (candidate.id === id ? updated : candidate));
    return mockDelay(clone(updated));
  },

  async deleteAdminPack(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/admin/vault/packs/${id}`);
      return;
    }
    assertMock('vaultApi.deleteAdminPack');
    requireMockPack(id);
    mockPacks = mockPacks.filter((candidate) => candidate.id !== id);
    mockActivity = {
      ...mockActivity,
      favoritePacks: mockActivity.favoritePacks.filter((candidate) => candidate !== id),
      downloads: mockActivity.downloads.filter((candidate) => candidate.packId !== id),
      recentPacks: mockActivity.recentPacks.filter((candidate) => candidate !== id),
      recentPrompts: mockActivity.recentPrompts.filter((candidate) => candidate.packId !== id),
      recentPresets: mockActivity.recentPresets.filter((candidate) => candidate.packId !== id),
    };
    await mockDelay(undefined);
  },

  async getAdminBonuses(): Promise<BonusPack[]> {
    if (!USE_MOCK_API) {
      return http.get<BonusPack[]>('/admin/bonuses');
    }
    assertMock('vaultApi.getAdminBonuses');
    return mockDelay(listMockBonuses({ includeUnpublished: true }));
  },

  async getAdminBonusClaims(id: string): Promise<BonusClaimRecipient[]> {
    if (!USE_MOCK_API) {
      return http.get<BonusClaimRecipient[]>(`/admin/bonuses/${id}/claims`);
    }
    assertMock('vaultApi.getAdminBonusClaims');
    return mockDelay([]);
  },

  async createAdminBonus(pack: BonusPack): Promise<BonusPack> {
    if (!USE_MOCK_API) {
      return http.post<BonusPack>('/admin/bonuses', pack);
    }
    assertMock('vaultApi.createAdminBonus');
    const created = createMockBonus({ ...clone(pack), id: pack.id || newId() });
    return mockDelay(created);
  },

  async previewAdminBonusEmail(pack: BonusPack): Promise<{ html: string }> {
    if (!USE_MOCK_API) {
      return http.post<{ html: string }>('/admin/bonuses/email/preview', pack);
    }
    assertMock('vaultApi.previewAdminBonusEmail');
    const email = pack.announcementEmail;
    return mockDelay({
      html: `<html><body><h1>${email?.heading ?? pack.name}</h1><p>${email?.message ?? pack.desc}</p></body></html>`,
    });
  },

  async updateAdminBonus(id: string, patch: Partial<BonusPack>): Promise<BonusPack> {
    if (!USE_MOCK_API) {
      return http.patch<BonusPack>(`/admin/bonuses/${id}`, patch);
    }
    assertMock('vaultApi.updateAdminBonus');
    const updated = updateMockBonus(id, patch);
    if (!updated) throw new ApiError(404, 'Bonus pack not found.', 'not_found');
    return mockDelay(updated);
  },

  async deleteAdminBonus(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/admin/bonuses/${id}`);
      return;
    }
    assertMock('vaultApi.deleteAdminBonus');
    const existing = getMockBonus(id, { includeUnpublished: true });
    if (!existing) throw new ApiError(404, 'Bonus pack not found.', 'not_found');
    deleteMockBonus(id);
    await mockDelay(undefined);
  },
};
